import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { initializePixel } from './utils/metaPixel';
import { captureAttributionData } from './utils/attributionTracker';
import './i18n'; // Initialize i18n
import Layout from './components/Layout/Layout';
import HomePage from './pages/HomePage';
import GuideToHpsPage from './pages/GuideToHpsPage';
import PartnerClubsPage from './pages/PartnerClubsPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AccountDeletionRequestPage from './pages/AccountDeletionRequestPage';
import GetAppPage from './pages/GetAppPage';
import PublicOrganisedEventPage from './pages/PublicOrganisedEventPage';
import { clearPlayerNextPath, readPlayerNextPath, rememberPlayerNextPath } from './utils/playerNextPath';
import RegisterPage from './pages/RegisterPage';
import RegisterPlayerPage from './pages/RegisterPlayerPage';
import CreatePlayerAccountPage from './pages/CreatePlayerAccountPage';
import LoginPage from './pages/LoginPage';
import PlayerLoginHelpPage from './pages/PlayerLoginHelpPage';
import PlayerSupportSessionPage from './pages/PlayerSupportSessionPage';
import PlayerRegistrationInvoicePage from './pages/PlayerRegistrationInvoicePage';
import DashboardPage from './pages/DashboardPage';
import PlayerDashboardLayout from './components/PlayerDashboard/PlayerDashboardLayout';
import PlayerDashboardPage from './pages/PlayerDashboardPage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import PlayerStatsPage from './pages/PlayerStatsPage';
import PlayerFindMatchPage from './pages/PlayerFindMatchPage';
import PlayerMatchDetailPage from './pages/PlayerMatchDetailPage';
import PlayerRankPage from './pages/PlayerRankPage';
import PlayerTournamentsLayout from './components/PlayerDashboard/PlayerTournamentsLayout';
import PlayerOrganisedTournamentDetailPage from './pages/PlayerOrganisedTournamentDetailPage';
import PlayerTeamsPage from './pages/PlayerTeamsPage';
import PlayerTeamWorkspacePage from './pages/PlayerTeamWorkspacePage';
import PlayerFindCoachPage from './pages/PlayerFindCoachPage';
import PlayerCoachBookingPage from './pages/PlayerCoachBookingPage';
import PlayerNotificationsPage from './pages/PlayerNotificationsPage';
import PlayerMessagesPage from './pages/PlayerMessagesPage';
import PlayerSettingsLayout from './components/PlayerDashboard/PlayerSettingsLayout';
import PlayerSecurityPage from './pages/PlayerSecurityPage';
import PlayerAvailabilityPage from './pages/PlayerAvailabilityPage';
import PlayerSettingsHubPage from './pages/PlayerSettingsHubPage';
import PlayerLeagueInfoLayout from './components/PlayerDashboard/PlayerLeagueInfoLayout';
import PlayerLeagueInfoHubPage from './pages/PlayerLeagueInfoHubPage';
import FindTeammatePage from './pages/FindTeammatePage';
import PlayerHelpWizardPage from './pages/PlayerHelpWizardPage';
import ResetPasswordModal from './components/ResetPasswordModal';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import SuperAdminTenantsPage from './pages/SuperAdminTenantsPage';
import AdminRouteGuard from './components/AdminRouteGuard';
import TestCheckoutPage from './pages/TestCheckoutPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';
import ScoreVerificationPage from './pages/ScoreVerificationPage';
import LoadingSpinner from './components/UI/LoadingSpinner';
import RouteTracker from './components/RouteTracker';
import UpdateNotification from './components/UpdateNotification';
import SkillLevelNotification from './components/SkillLevelNotification';
import versionService from './services/versionService';

/** Logged-in players must use /player/register-team (sidebar layout), not public /register-team. */
function RedirectPlayerRegisterTeamToDashboard() {
  const { search } = useLocation();
  return <Navigate to={`/player/register-team${search}`} replace />;
}

function PlayerTeamWorkspaceOverviewRedirect() {
  const { teamId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/player/team/${teamId}/overview${search}`} replace />;
}

/** Where to send a logged-in player from public auth routes (profile first if incomplete). */
function playerPostAuthHomePath(user) {
  if (!user || user.type !== 'player') return '/player';
  return user.profileComplete === false ? '/player/settings/profile' : '/player';
}

function LoginOrContinue({ user }) {
  const [params] = useSearchParams();
  if (!user) return <Layout><LoginPage /></Layout>;
  const next = readPlayerNextPath(params.get('next'));
  if (user.type === 'player' && next) {
    clearPlayerNextPath();
    return <Navigate to={next} replace />;
  }
  return <Navigate to={playerPostAuthHomePath(user)} replace />;
}

function RequirePlayerLayout({ user }) {
  const loc = useLocation();
  if (!user) {
    const next = `${loc.pathname}${loc.search || ''}`;
    if (next.startsWith('/player/')) {
      rememberPlayerNextPath(next);
      return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }
  if (user.type !== 'player') return <Navigate to="/dashboard" replace />;
  return <PlayerDashboardLayout />;
}

function App() {
  const { user, loading } = useAuth();

  // Initialize Facebook Pixel, capture attribution data, and start version checking on app load
  useEffect(() => {
    initializePixel();
    captureAttributionData();
    
    // Start version checking service
    versionService.startVersionChecking();
    
    // Cleanup version checking on unmount
    return () => {
      versionService.stopVersionChecking();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminAuthProvider>
      <RouteTracker />
      <UpdateNotification />
      <SkillLevelNotification />
      <Routes>
        {/* Admin Login - No tenant in URL (tenant detected after login) */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Super Admin: Tenant list + create (Super Admin only; others redirected) */}
        <Route
          path="/admin/tenants"
          element={
            <AdminRouteGuard>
              <SuperAdminTenantsPage />
            </AdminRouteGuard>
          }
        />

        {/* Admin Dashboard - With tenant in URL (e.g., /switzerland/admin/dashboard) */}
        <Route
          path="/:tenant/admin/dashboard"
          element={
            <AdminRouteGuard>
              <AdminDashboardPage />
            </AdminRouteGuard>
          }
        />

        {/* Admin Routes - Fallback without tenant (backward compatibility) */}
        <Route
          path="/admin/dashboard"
          element={
            <AdminRouteGuard>
              <AdminDashboardPage />
            </AdminRouteGuard>
          }
        />

      {/* Team Routes - Use team layout */}
      <Route 
        path="/*" 
        element={
            <Routes>
              {/* Public chrome-less routes for the mobile app WebView (no Header/Footer) */}
              <Route path="/app-info" element={<HomePage embedded />} />
              <Route path="/app-info/guide-to-hps" element={<GuideToHpsPage embedded />} />
              <Route path="/app-info/how-league-works" element={<Navigate to="/app-info/guide-to-hps" replace />} />
              <Route path="/app-info/partner-clubs" element={<PartnerClubsPage embedded />} />
              <Route path="/app-info/privacy-policy" element={<PrivacyPolicyPage embedded appEmbed />} />
              <Route path="/app-info/terms-and-conditions" element={<TermsAndConditionsPage embedded appEmbed />} />

              {/* Public routes with layout */}
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/guide-to-hps" element={<Layout enableScrollMemory={true}><GuideToHpsPage /></Layout>} />
              <Route path="/how-league-works" element={<Navigate to="/guide-to-hps" replace />} />
              <Route path="/rules" element={<Navigate to="/guide-to-hps" replace />} />
              <Route path="/seasons" element={<Navigate to="/guide-to-hps" replace />} />
              <Route path="/partner-clubs" element={<Layout><PartnerClubsPage /></Layout>} />
              <Route path="/terms-and-conditions" element={<Layout enableScrollMemory={true}><TermsAndConditionsPage /></Layout>} />
              <Route path="/privacy-policy" element={<Layout enableScrollMemory={true}><PrivacyPolicyPage /></Layout>} />
              <Route path="/account-deletion-request" element={<Layout enableScrollMemory={true}><AccountDeletionRequestPage /></Layout>} />
              <Route path="/get-app" element={<Layout><GetAppPage /></Layout>} />
              <Route path="/e/:eventId" element={<Layout><PublicOrganisedEventPage /></Layout>} />
              <Route path="/event/:eventId" element={<Layout><PublicOrganisedEventPage /></Layout>} />
              <Route path="/download-app" element={<Navigate to="/get-app" replace />} />
              <Route path="/app" element={<Navigate to="/get-app" replace />} />
              <Route 
                path="/register" 
                element={user ? <Navigate to={playerPostAuthHomePath(user)} replace /> : <Layout><RegisterPage /></Layout>} 
              />
              <Route
                path="/register-team"
                element={
                  user?.type === 'player' ? (
                    <RedirectPlayerRegisterTeamToDashboard />
                  ) : user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Layout>
                      <RegisterPage />
                    </Layout>
                  )
                }
              />
              <Route 
                path="/find-teammate" 
                element={<Layout><FindTeammatePage /></Layout>} 
              />
              <Route 
                path="/register-player" 
                element={user ? <Navigate to={playerPostAuthHomePath(user)} replace /> : <Layout><RegisterPlayerPage /></Layout>} 
              />
              <Route 
                path="/create-account" 
                element={user ? <Navigate to={playerPostAuthHomePath(user)} replace /> : <Layout><CreatePlayerAccountPage /></Layout>} 
              />
              <Route
                path="/login"
                element={<LoginOrContinue user={user} />}
              />
              <Route
                path="/player-login-help"
                element={user ? <Navigate to={playerPostAuthHomePath(user)} replace /> : <Layout><PlayerLoginHelpPage /></Layout>}
              />
              <Route path="/reset-password" element={<Layout><ResetPasswordModal /></Layout>} />
              <Route path="/test-checkout" element={<Layout><TestCheckoutPage /></Layout>} />
              <Route path="/payment" element={<Layout><PaymentPage /></Layout>} />
              <Route path="/payment-success" element={<Layout><PaymentSuccessPage /></Layout>} />
              <Route path="/payment-cancelled" element={<Layout><PaymentCancelledPage /></Layout>} />
              <Route path="/test-payment-success" element={<Layout><PaymentSuccessPage /></Layout>} />
              
              {/* Score verification route - no layout needed */}
              <Route path="/verify-score/:verificationToken" element={<ScoreVerificationPage />} />
              
              {/* Protected routes without layout (they have their own) */}
              <Route 
                path="/dashboard" 
                element={user ? <DashboardPage /> : <Navigate to="/login" replace />} 
              />

              <Route
                path="/player/support-session"
                element={<PlayerSupportSessionPage />}
              />

              {/* Phase 2: Player dashboard — team sessions must use /dashboard */}
              <Route
                path="/player"
                element={<RequirePlayerLayout user={user} />}
              >
                <Route index element={<PlayerDashboardPage />} />
                <Route path="profile" element={<Navigate to="/player/settings/profile" replace />} />
                <Route path="stats" element={<PlayerStatsPage />} />
                <Route path="find-match" element={<PlayerFindMatchPage />} />
                <Route path="matches/:matchId" element={<PlayerMatchDetailPage />} />
                <Route path="messages" element={<PlayerMessagesPage />} />
                <Route path="messages/:conversationId" element={<PlayerMessagesPage />} />
                <Route path="rank" element={<PlayerRankPage />} />
                <Route path="tournaments" element={<PlayerTournamentsLayout />} />
                <Route path="events/tournaments/:eventId" element={<PlayerOrganisedTournamentDetailPage />} />
                <Route path="coaches" element={<PlayerFindCoachPage />} />
                <Route path="coaches/:coachId/book" element={<PlayerCoachBookingPage />} />
                <Route path="find-teammate" element={<FindTeammatePage embedded />} />
                <Route path="help" element={<PlayerHelpWizardPage />} />
                <Route path="teams" element={<PlayerTeamsPage />} />
                <Route path="team/:teamId" element={<PlayerTeamWorkspaceOverviewRedirect />} />
                <Route path="team/:teamId/:section" element={<PlayerTeamWorkspacePage />} />
                <Route path="register-team" element={<RegisterPage />} />
                <Route path="payment-success" element={<PaymentSuccessPage />} />
                <Route path="payment-cancelled" element={<PaymentCancelledPage />} />
                <Route path="notifications" element={<PlayerNotificationsPage />} />
                <Route path="registration-invoice/:invoiceId" element={<PlayerRegistrationInvoicePage />} />
                <Route path="settings" element={<PlayerSettingsLayout />}>
                  <Route index element={<PlayerSettingsHubPage />} />
                  <Route path="profile" element={<PlayerProfilePage />} />
                  <Route path="security" element={<PlayerSecurityPage />} />
                  <Route path="availability" element={<PlayerAvailabilityPage />} />
                </Route>
                <Route path="league-info" element={<PlayerLeagueInfoLayout />}>
                  <Route index element={<PlayerLeagueInfoHubPage />} />
                  <Route path="guide-to-hps" element={<GuideToHpsPage embedded />} />
                  <Route path="how-league-works" element={<Navigate to="/player/league-info/guide-to-hps" replace />} />
                  <Route path="rules" element={<Navigate to="/player/league-info/guide-to-hps" replace />} />
                  <Route path="seasons" element={<Navigate to="/player/league-info/guide-to-hps" replace />} />
                  <Route path="partner-clubs" element={<PartnerClubsPage embedded />} />
                  <Route path="terms-and-conditions" element={<TermsAndConditionsPage embedded />} />
                  <Route path="privacy-policy" element={<PrivacyPolicyPage embedded />} />
                </Route>
              </Route>
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        } 
      />
      </Routes>
    </AdminAuthProvider>
  );
}

export default App;
